import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { establishSession, endSession, fetchSnapshot, sendMutation, type MutationResponse, type Session } from '../api/client';
import {
  activeSet,
  DomainError,
  emptyAccount,
  mutationSchema,
  publicSnapshot,
  type Mutation,
  type Snapshot,
} from '../domain/model';
import { executeMutation } from '../domain/mutations';
import { taipeiDate } from '../domain/schedule';
import {
  acknowledge,
  enqueue,
  eventsFor,
  loadSnapshot,
  namespace,
  retainProblem,
  saveLocal,
  saveSnapshot,
  type StoredEvent,
} from '../storage/local';
import { drainEvents } from '../storage/sync';
import { NOTICE_EVENT } from './notice';
import { clearStoredSession, readStoredSession, saveStoredSession, validStoredSession } from './storedSession';

type AppContext = {
  session: Session | null;
  snapshot: Snapshot | null;
  loading: boolean;
  refreshing?: boolean;
  error: string;
  online: boolean;
  events: StoredEvent[];
  notice: string;
  login: (uid: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
  write: (
    body: Record<string, unknown>,
    offlineAllowed?: boolean,
    background?: boolean,
  ) => Promise<MutationResponse | null>;
  refresh: () => Promise<void>;
  sync: () => Promise<number>;
  discardProblem: (event: StoredEvent) => Promise<void>;
  notify: (message: string, durationMs?: number) => void;
};
const Context = createContext<AppContext | null>(null);
export const useApp = () => {
  const context = useContext(Context);
  if (!context) throw new Error('Missing session provider');
  return context;
};
async function overlay(snapshot: Snapshot): Promise<Snapshot> {
  let account = { ...emptyAccount(snapshot.uid), ...snapshot };
  for (const event of await eventsFor(namespace(snapshot.uid)))
    if (event.state === 'pending') {
      try {
        let generated = 0;
        account = executeMutation(
          account,
          event.mutation,
          Date.now(),
          () => `pending-${event.mutation.mutationId}-${++generated}`,
        ).account;
      } catch {
        /* Conflicts stay in the outbox until authoritative synchronization. */
      }
    }
  return publicSnapshot(account);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [session, setSession] = useState<Session | null>(() => {
    const stored = readStoredSession();
    return stored ? { ...stored, generation: 1, controller: new AbortController() } : null;
  });
  const current = useRef<Session | null>(session);
  const generation = useRef(session?.generation ?? 0);
  const mounted = useRef(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [events, setEvents] = useState<StoredEvent[]>([]);
  const [notice, setNotice] = useState('');
  const noticeTimer = useRef<number | undefined>(undefined);
  const notify = useCallback((message: string, durationMs = 3000) => {
    if (noticeTimer.current !== undefined) window.clearTimeout(noticeTimer.current);
    noticeTimer.current = undefined;
    setNotice(message);
    if (message && durationMs) {
      noticeTimer.current = window.setTimeout(() => {
        setNotice((current) => current === message ? '' : current);
        noticeTimer.current = undefined;
      }, durationMs);
    }
  }, []);
  useEffect(() => () => {
    if (noticeTimer.current !== undefined) window.clearTimeout(noticeTimer.current);
  }, []);
  useEffect(() => {
    const show = (event: Event) => notify(String((event as CustomEvent).detail));
    window.addEventListener(NOTICE_EVENT, show);
    return () => window.removeEventListener(NOTICE_EVENT, show);
  }, [notify]);
  const [readyScope, setReadyScope] = useState('');
  const snapshotRef = useRef<Snapshot | null>(null);
  const scope = session ? namespace(session.uid) : '';
  const reportedProblem = useRef('');
  const updateEvents = useCallback(async (owner: Session) => {
    const queued = await eventsFor(namespace(owner.uid));
    if (current.current !== owner) return;
    setEvents(queued);
    const problems = queued.filter((event) => event.state !== 'pending');
    const fingerprint = problems.map((event) => `${event.key}:${event.message}`).join('|');
    if (fingerprint && fingerprint !== reportedProblem.current)
      notify('同步失敗；有紀錄未套用，請到帳戶查看或捨棄', 5000);
    reportedProblem.current = fingerprint;
  }, [notify]);
  const query = useQuery({
    queryKey: ['snapshot', scope, session?.generation],
    enabled: !!session && readyScope === `${session.uid}|${session.generation}`,
    queryFn: async ({ signal }) => {
      const owner = session!;
      let data: Snapshot | null;
      if (navigator.onLine && (owner.token || owner.persistent)) {
        data = await fetchSnapshot(owner, signal);
        if (current.current !== owner || signal.aborted)
          throw new DOMException('Query superseded', 'AbortError');
        await saveSnapshot(namespace(owner.uid), data);
      } else data = await loadSnapshot(namespace(owner.uid));
      if (!data) throw new Error('尚無此帳號的離線資料，請連線登入');
      return overlay(data);
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });
  useEffect(() => {
    if (!session) return;
    const owner = session;
    const key = ['snapshot', namespace(owner.uid), owner.generation];
    let active = true;
    void loadSnapshot(namespace(owner.uid)).then(async (cached) => {
      if (cached && active && current.current === owner && !client.getQueryData(key)) {
        const restored = await overlay(cached);
        if (active && current.current === owner && !client.getQueryData(key))
          client.setQueryData(key, restored, { updatedAt: 0 });
      }
    }).catch(() => undefined).finally(() => {
      if (active && current.current === owner)
        setReadyScope(`${owner.uid}|${owner.generation}`);
    });
    return () => { active = false; };
  }, [client, session]);
  const snapshot = query.data ?? null;
  snapshotRef.current = snapshot;
  const login = useCallback(
    async (uid: string, token: string) => {
      const credentials = !__MOCK__ && validStoredSession({ uid, token })
        ? await establishSession(uid, token) : { uid, token };
      current.current?.controller.abort();
      await client.cancelQueries();
      client.clear();
      snapshotRef.current = null;
      setEvents([]);
      setNotice('');
      reportedProblem.current = '';
      const owner: Session = {
        ...credentials,
        generation: ++generation.current,
        controller: new AbortController(),
      };
      current.current = owner;
      await saveLocal('lexispulse-test', 'last-account', uid);
      if (navigator.onLine && (owner.token || owner.persistent)) {
        try {
          await drainEvents(
            namespace(uid),
            (event) => sendMutation(owner, event.mutation),
            () => current.current === owner,
          );
        } catch (error) {
          if (current.current === owner)
            notify(error instanceof Error ? `同步失敗：${error.message}` : '同步失敗，紀錄已保留', 5000);
        }
      }
      if (current.current !== owner) return;
      saveStoredSession(credentials);
      setSession(owner);
      await updateEvents(owner);
    },
    [client, notify, updateEvents],
  );
  const logout = useCallback(async () => {
    if (current.current?.persistent && navigator.onLine) {
      try { await endSession(current.current.uid); }
      catch { setNotice('無法連線完成登出，請稍後再試'); return; }
    }
    clearStoredSession();
    current.current?.controller.abort();
    current.current = null;
    generation.current++;
    await client.cancelQueries();
    client.clear();
    snapshotRef.current = null;
    setSession(null);
    setEvents([]);
    setNotice('');
    reportedProblem.current = '';
    await saveLocal('lexispulse-test', 'last-account', null);
  }, [client]);
  useEffect(() => {
    if (!session) return;
    void updateEvents(session);
    if (!validStoredSession(session)) return;
    const checkExpiry = () => {
      if (current.current === session && !validStoredSession(session)) void logout();
    };
    const timer = window.setInterval(checkExpiry, 15000);
    window.addEventListener('focus', checkExpiry);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', checkExpiry); };
  }, [session, logout, updateEvents]);
  useEffect(() => {
    if (query.error instanceof DomainError && query.error.code === 'unauthorized') void logout();
  }, [query.error, logout]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // StrictMode immediately replays effects; only abort a real unmount.
      queueMicrotask(() => { if (!mounted.current) current.current?.controller.abort(); });
    };
  }, []);
  const refresh = useCallback(async () => {
    await client.invalidateQueries({ queryKey: ['snapshot'] });
  }, [client]);
  const sync = useCallback(async () => {
    const owner = current.current;
    if (!owner) return 0;
    const ownerScope = namespace(owner.uid);
    if (navigator.onLine) {
      try {
        await drainEvents(
          ownerScope,
          (event) => sendMutation(owner, event.mutation),
          () => current.current === owner,
        );
      } catch (error) {
        await updateEvents(owner);
        if (current.current === owner)
          notify(error instanceof Error ? `同步失敗：${error.message}` : '同步失敗，紀錄已保留', 5000);
        throw error;
      }
    }
    const remaining = await eventsFor(ownerScope);
    if (current.current === owner) {
      setEvents(remaining);
      if (remaining.some((event) => event.state !== 'pending'))
        notify('同步失敗；有紀錄未套用，請到帳戶查看或捨棄', 5000);
      if (navigator.onLine) await refresh();
    }
    return remaining.length;
  }, [notify, refresh, updateEvents]);
  const discardProblem = useCallback(async (event: StoredEvent) => {
    const owner = current.current;
    if (!owner || event.scope !== namespace(owner.uid) || event.state === 'pending')
      throw new Error('只能捨棄目前帳號需要處理的紀錄');
    const latest = (await eventsFor(event.scope)).find((item) => item.key === event.key);
    if (!latest || latest.state === 'pending')
      throw new Error('紀錄狀態已改變，請重新整理');
    await acknowledge(latest);
    await updateEvents(owner);
  }, [updateEvents]);
  const write = useCallback(
    async (
      body: Record<string, unknown>,
      offlineAllowed = false,
      background = false,
    ): Promise<MutationResponse | null> => {
      const owner = current.current;
      const data = snapshotRef.current;
      if (!owner || !data) throw new Error('請先登入');
      if ((!navigator.onLine || (!owner.token && !owner.persistent)) && !offlineAllowed)
        throw new Error('請連線登入後保存，輸入內容會保留');
      if (body.operation === 'restartWords') {
        await sync();
        if ((await eventsFor(namespace(owner.uid))).length)
          throw new Error('有待同步或衝突紀錄，請先處理後再重新開始');
      }
      if (current.current !== owner) throw new Error('帳號已切換');
      const now = Date.now();
      const command = mutationSchema.parse({
        schemaVersion: 2,
        mutationId: crypto.randomUUID(),
        sessionVersion: data.sessionVersion,
        studyDate: taipeiDate(now),
        occurredAt: now,
        ...body,
      }) as Mutation;
      let generated = 0;
      const optimistic = executeMutation(
        { ...emptyAccount(data.uid), ...data }, command, now,
        () => `pending-${command.mutationId}-${++generated}`,
      ).account;
      const next = publicSnapshot(optimistic);
      const event = await enqueue(namespace(owner.uid), command);
      await updateEvents(owner);
      if (current.current === owner) {
        snapshotRef.current = next;
        client.setQueryData(['snapshot', namespace(owner.uid), owner.generation], next);
      }
      if (!navigator.onLine || (!owner.token && !owner.persistent)) {
        if (!background) setNotice('已保存在此裝置，連線登入後同步');
        return null;
      }
      const synchronize = async () => {
        try {
          const result = await sendMutation(owner, command);
          await acknowledge(event);
          await updateEvents(owner);
          if (current.current === owner && result.snapshot) {
            snapshotRef.current = result.snapshot;
            client.setQueryData(['snapshot', namespace(owner.uid), owner.generation], result.snapshot);
            void saveSnapshot(namespace(owner.uid), result.snapshot).catch(() => {
              if (current.current === owner) setNotice('已保存；此裝置快取稍後更新');
            });
          } else if (current.current === owner)
            void refresh().catch(() => {
              if (current.current === owner) setNotice('已保存；最新資料稍後再更新');
            });
          return result;
        } catch (error) {
          if (
            error instanceof DomainError &&
            (error.code === 'conflict' || error.code === 'invalid')
          ) {
            await retainProblem(event, error.code, error.message);
            await updateEvents(owner);
            await refresh();
          }
          throw error;
        }
      };
      if (background) {
        void synchronize().catch((error) => {
          if (current.current === owner)
            setNotice(error instanceof DomainError ? error.message : '同步暫時失敗，紀錄已保留');
        });
        return null;
      }
      return synchronize();
    },
    [client, refresh, sync, updateEvents],
  );
  useEffect(() => {
    const connected = () => {
      setOnline(true);
      void sync().catch(() => undefined);
    };
    const disconnected = () => setOnline(false);
    window.addEventListener('online', connected);
    window.addEventListener('offline', disconnected);
    return () => {
      window.removeEventListener('online', connected);
      window.removeEventListener('offline', disconnected);
    };
  }, [sync]);
  useEffect(() => {
    if (snapshot && snapshot.sets.some((set) => !activeSet(snapshot, set))) setNotice('快取已更新');
  }, [snapshot]);
  return (
    <Context.Provider
      value={{
        session,
        snapshot,
        loading: !!session && (readyScope !== `${session.uid}|${session.generation}` || query.isLoading),
        refreshing: query.isFetching,
        error: query.error?.message ?? '',
        online,
        events,
        notice,
        login,
        logout,
        write,
        refresh,
        sync,
        discardProblem,
        notify,
      }}
    >
      {children}
    </Context.Provider>
  );
}
